import { useFormikContext } from "formik";
import React, { useCallback, useEffect, useRef } from "react";
import { ConfirmOptions, useConfirm } from "stories/Admin";
import { CampaignFormValues, isCampaignFormDirty } from "./campaignKind";

/** Spørsmålet før ulagrede endringer i kampanjeskjemaet forkastes. */
export const DISCARD_CHANGES_CONFIRM: ConfirmOptions = {
  title: "Forkaste endringene?",
  message: "Endringene du har gjort blir ikke lagret.",
  confirmLabel: "Forkast",
  cancelLabel: "Fortsett å redigere",
  tone: "danger",
};

/** Nettleseren spør før siden lukkes eller lastes på nytt så lenge `active`. */
export const useBeforeUnload = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Eldre nettlesere trenger returnValue for å vise spørsmålet.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [active]);
};

/**
 * Lukking av et skjema som kan ha ulagrede endringer. `requestClose` spør
 * «Forkaste endringene?» bare når skjemaet er endret, og kaller `onClose`
 * hvis brukeren forkaster (eller ingenting er endret). Lagring skal kalle
 * `onClose` direkte, uten å spørre.
 */
export const useDiscardGuard = (onClose: () => void) => {
  const confirm = useConfirm();
  const dirtyRef = useRef(false);
  const askingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const setDirty = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const isDirty = useCallback(() => dirtyRef.current, []);

  const requestClose = useCallback(async () => {
    // Escape og klikk utenfor kan komme mens spørsmålet allerede vises.
    if (askingRef.current) return;
    if (dirtyRef.current) {
      askingRef.current = true;
      try {
        if (!(await confirm(DISCARD_CHANGES_CONFIRM))) return;
      } finally {
        askingRef.current = false;
      }
    }
    dirtyRef.current = false;
    onCloseRef.current();
  }, [confirm]);

  return { requestClose, setDirty, isDirty };
};

/**
 * Legges inne i et kampanjeskjema (Formik). Melder om skjemaet er endret, og
 * ber nettleseren spørre før siden lukkes mens det er ulagrede endringer.
 */
export const UnsavedChangesGuard: React.FC<{
  onDirtyChange?: (dirty: boolean) => void;
}> = ({ onDirtyChange }) => {
  const { values, initialValues } = useFormikContext<CampaignFormValues>();
  const dirty = isCampaignFormDirty(initialValues, values);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  useBeforeUnload(dirty);
  return null;
};
